import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { useVehicleReservation } from "../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useResponsiveValues } from "../../../../common/Providers/DimentionsProvider/UseResponsiveValues";
import CustomDatePicker from "../../../../common/CustomDatePicker";

interface props {
  sx?: object;
  contentStyle?: object;
  disabledDates?: string[];
  onMonthChange?: (dateRange: { start: Date; end: Date; selectedDate?: string }) => void;
  minSelectableDate?: string; // YYYY-MM-DD from API cut_off_passed logic
  currentDate?: string;       // YYYY-MM-DD from API (store's timezone, e.g. America/New_York)
}

const DateSelector: React.FC<props> = ({ sx, contentStyle, disabledDates = [], onMonthChange, minSelectableDate, currentDate }) => {
  const { VehicleReservationMain, setVehicleReservationValue } = useVehicleReservation();
  const { matches900 } = useResponsiveValues();
  const [minDate, setMinDate] = useState(() => {
    // Trust API's min_selectable_date as source of truth (store timezone)
    if (minSelectableDate) return dayjs(minSelectableDate).set('hour', 0).set('minute', 0).set('second', 0);
    return dayjs().set('hour', 0).set('minute', 0).set('second', 0);
  });

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  // Sync selectedDate with VehicleReservationMain.pickup when component mounts or pickup changes
  useEffect(() => {
    const today = dayjs();

    if (VehicleReservationMain.pickup) {
      const pickupDate = dayjs(VehicleReservationMain.pickup);
      setSelectedDate(pickupDate);
      // Set current month to the pickup date's month for better UX
      setCurrentMonth(pickupDate);

      const isCurrentMonth = pickupDate.isSame(today, 'month');
      // Current month: today → end of month | Other months: 1st → last
      const startDate = isCurrentMonth ? today : pickupDate.startOf('month');
      const endDate = pickupDate.endOf('month');

      onMonthChange?.({
        start: startDate.toDate(),
        end: endDate.toDate(),
        selectedDate: pickupDate.format('YYYY-MM-DD'),
      });
    } else {
      // No pickup date — use today as selectedDate, current month range
      const startDate = today;
      const endDate = today.endOf('month');

      onMonthChange?.({
        start: startDate.toDate(),
        end: endDate.toDate(),
        selectedDate: today.format('YYYY-MM-DD'),
      });
    }
  }, [VehicleReservationMain.pickup]);

  useEffect(() => {
    // Trust API's min_selectable_date as source of truth (store timezone)
    if (minSelectableDate) {
      setMinDate(dayjs(minSelectableDate).set('hour', 0).set('minute', 0).set('second', 0));
    } else {
      setMinDate(dayjs().set('hour', 0).set('minute', 0).set('second', 0));
    }
  }, [minSelectableDate, currentDate]);

  // Check if date is available - API is the single source of truth
  const isDateAvailable = (date: Dayjs) => {
    // Check if date is before today
    if (date.isBefore(minDate, 'day')) {
      return false;
    }

    // Check if date is in disabled_dates from API response
    const dateStr = date.format('YYYY-MM-DD');
    if (disabledDates.includes(dateStr)) {
      return false;
    }

    return true;
  };

  const handleDateChange = (newDate: any) => {
    if (newDate) {
      const pickupDateTime = new Date(newDate.toISOString());

      if (VehicleReservationMain.pickup_time) {
        const [hours, minutes] = VehicleReservationMain.pickup_time.split(':').map(Number);
        pickupDateTime.setHours(hours, minutes, 0, 0);
      } else {
        pickupDateTime.setHours(0, 0, 0, 0);
      }

      setVehicleReservationValue('pickup', pickupDateTime);

      // Keep date range same, only update selectedDate
      if (onMonthChange) {
        const today = dayjs();
        const clickedDate = dayjs(newDate);
        const isCurrentMonth = currentMonth.isSame(today, 'month');

        const startDate = isCurrentMonth ? today : currentMonth.startOf('month');
        const endDate = currentMonth.endOf('month');

        onMonthChange({
          start: startDate.toDate(),
          end: endDate.toDate(),
          selectedDate: clickedDate.format('YYYY-MM-DD'),
        });
      }
    }
  };

  const shouldDisableDate = (date: Date) => {
    const dayjsDate = dayjs(date);
    return !isDateAvailable(dayjsDate);
  };

  const navigateMonth = useCallback((direction: "prev" | "next") => {
    let newMonth;
    if (direction === "prev") {
      newMonth = currentMonth.subtract(1, "month");
    } else {
      newMonth = currentMonth.add(1, "month");
    }
    setCurrentMonth(newMonth);

    const today = dayjs();
    const isCurrentMonth = newMonth.isSame(today, 'month');

    // Current month: today → end of month | Other months: 1st → last
    const startDate = isCurrentMonth ? today : newMonth.startOf('month');
    const endDate = newMonth.endOf('month');

    // selectedDate = 1st of new month (or today if navigating back to current month)
    const selectedDate = isCurrentMonth
      ? today.format('YYYY-MM-DD')
      : newMonth.startOf('month').format('YYYY-MM-DD');

    onMonthChange?.({
      start: startDate.toDate(),
      end: endDate.toDate(),
      selectedDate,
    });
  }, [currentMonth, onMonthChange]);

  // Called by MUI DatePicker when user clicks chevrons ⬅️➡️
  const handlePickerMonthChange = useCallback((newMonth: Dayjs) => {
    setCurrentMonth(newMonth);

    const today = dayjs();
    const isCurrentMonth = newMonth.isSame(today, 'month');

    // Current month: today → end of month | Other months: 1st → last
    const startDate = isCurrentMonth ? today : newMonth.startOf('month');
    const endDate = newMonth.endOf('month');

    // selectedDate = 1st of new month (or today if navigating back to current month)
    const selectedDate = isCurrentMonth
      ? today.format('YYYY-MM-DD')
      : newMonth.startOf('month').format('YYYY-MM-DD');

    console.log(`📅 Chevron navigation → ${newMonth.format('MMMM YYYY')}, range: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}, selectedDate: ${selectedDate}`);

    onMonthChange?.({
      start: startDate.toDate(),
      end: endDate.toDate(),
      selectedDate,
    });
  }, [onMonthChange]);

  const generateCalendarDays = (month: Dayjs) => {
    const startOfMonth = month.startOf("month");
    const endOfMonth = month.endOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfWeek = endOfMonth.endOf("week");

    const days = [];
    let current = startOfWeek;

    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, "day")) {
      days.push(current);
      current = current.add(1, "day");
    }

    return days;
  };

  return (
    <Box sx={sx}>
      {/* <Typography sx={{ fontSize: '16px', marginBottom: '10px', fontWeight: 'bold' }}>Select Pickup Date</Typography> */}
      <CustomDatePicker
        name=""
        sx={{ width: matches900 ? '300px' : '100%' }}
        value={selectedDate || dayjs(VehicleReservationMain.pickup)}
        onChange={handleDateChange}
        onMonthChange={handlePickerMonthChange}
        minDate={dayjs('2000-01-01')}
        shouldDisableDate={shouldDisableDate}
      />
    </Box>
  );
};

const styles = (matches900: boolean) => ({
  scheduleContainer: {
    width: "100%",
    padding: "16px",
    flex: 1,
    overflow: "auto",
  },
  scheduleHeader: {
    minHeight: "72px",
  },
  scheduleHeaderTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  scheduleSubtitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: matches900 ? "repeat(2, 1fr)" : "1fr",
    gap: "24px",
    marginBottom: "32px",
  },
  productCard: {
    borderRadius: "4px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
  },
  imageContainer: {
    width: "100%",
    height: "210px",
    borderRadius: "4px",
    overflow: "hidden",
    backgroundColor: "#f8f8f8",
    position: "relative",
  },
  imagePlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0",
    borderRadius: "8px",
  },
  productDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "8px 20px 20px 20px",
  },
  productName: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    textAlign: "left",
    lineHeight: 1.3,
  },
  quantityControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "12px",
  },
  quantityButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "2px solid #2196F3",
    backgroundColor: "#ffffff",
    color: "#2196F3",
    "&:hover": {
      backgroundColor: "#2196F3",
      color: "#ffffff",
    },
  },
  quantityDisplay: {
    minWidth: "60px",
    textAlign: "center",
    borderBottom: "2px solid #2196F3",
    paddingBottom: "4px",
  },
  quantityText: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#333",
  },
  dateTimeSection: {
    marginTop: "24px",
  },
  timeSection: {
    marginTop: "32px",
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "8px",
  },
  calendarContainer: {
    display: "grid",
    gridTemplateColumns: matches900 ? "1fr 1fr" : "1fr",
    gap: "24px",
    marginBottom: "16px",
  },
  monthCalendar: {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    padding: "16px",
    // border: "1px solid #e0e0e0",
  },
  monthHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  monthTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
  },
  weekDays: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    marginBottom: "8px",
  },
  weekDay: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#666",
    textAlign: "center",
    padding: "8px 4px",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
  },
  calendarDay: {
    minWidth: "32px",
    height: "32px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#333",
    backgroundColor: "transparent",
    border: "none",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  availableDay: {
    backgroundColor: "#4CAF50 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#45a049 !important",
    },
  },
  selectedDay: {
    backgroundColor: "#2196F3 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#1976D2 !important",
    },
  },
  disabledDay: {
    color: "#ccc !important",
    backgroundColor: "transparent !important",
    "&:hover": {
      backgroundColor: "transparent !important",
    },
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "16px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  legendColor: {
    width: "16px",
    height: "16px",
    backgroundColor: "#4CAF50",
    borderRadius: "2px",
  },
  legendText: {
    fontSize: "14px",
    color: "#666",
  },
  timeSlots: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  timeSlot: {
    padding: "2px 4px",
    borderRadius: "4px",
    border: "2px solid #2196F3",
    backgroundColor: "#ffffff",
    color: "#2196F3",
    fontSize: "16px",
    fontWeight: 500,
    minWidth: "100px",
    "&:hover": {
      backgroundColor: "#f0f8ff",
    },
    "&:disabled": {
      borderColor: "#e0e0e0",
      color: "#ccc",
      backgroundColor: "#f5f5f5",
    },
  },
  selectedTimeSlot: {
    backgroundColor: "#2196F3 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#1976D2 !important",
    },
  },
  durationSection: {
    marginBottom: "32px",
  },
  durationDropdownContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "600px",
  },
  durationDropdown: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    border: "2px solid #0047C0",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  durationDropdownText: {
    fontSize: "16px",
    color: "#333",
    fontWeight: 500,
  },
  durationDropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    border: "2px solid #0047C0",
    borderTop: "none",
    borderRadius: "0 0 4px 4px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    zIndex: 1000,
    maxHeight: "300px",
    overflowY: "auto",
  },
  durationOption: {
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#f0f8ff",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  selectedDurationOption: {
    backgroundColor: "#0047C0 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#003d9f !important",
    },
  },
  durationOptionText: {
    fontSize: "16px",
    fontWeight: 400,
  },
});

export default DateSelector;
